import { Link } from 'react-router-dom';
import { Check, Smartphone, Sparkles } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { SectionHead } from '@/components/landing/Sections';
import { MOBILE_APPS, APP_SCREENS } from '@/config/mobileApps';
import { Logo } from '@/components/Logo';
import { SUPPORT_EMAIL } from '@/components/legal/LegalShell';

function StoreBadge({ app, label, testId }) {
  const inner = (
    <>
      <Smartphone className="h-6 w-6 text-brand" />
      <span className="text-left leading-tight">
        <span className="block text-[11px] uppercase tracking-wider text-white/50">{app.url ? 'Télécharger sur' : 'Prochainement'}</span>
        <span className="block font-display font-semibold text-white">{app.url ? label : app.comingSoon}</span>
      </span>
    </>
  );
  const cls = 'flex items-center justify-center gap-3 rounded-2xl border border-white/10 bg-black/60 px-5 py-3';
  return app.url
    ? <a href={app.url} target="_blank" rel="noreferrer" className={`${cls} hover:border-brand/60`} data-testid={testId}>{inner}</a>
    : <div className={`${cls} cursor-default`} aria-disabled="true" data-testid={testId}>{inner}</div>;
}

function Shot({ src, alt, className, testId }) {
  return (
    <img src={src} alt={alt} loading="lazy" width="720" height="1280" draggable={false} data-testid={testId}
         className={`block aspect-[9/16] w-full rounded-[1.75rem] object-contain shadow-[0_40px_80px_-24px_rgba(0,0,0,0.9),0_0_0_1px_rgba(255,255,255,0.06)] ${className}`} />
  );
}

export function MobileApps() {
  return (
    <section id="mobile" className="scroll-mt-24 py-24" data-testid="landing-mobile">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="relative overflow-hidden rounded-[2.5rem] border border-white/5 bg-gradient-to-br from-[#16120a] via-surface-app to-black px-5 py-12 sm:p-14">
          <div className="pointer-events-none absolute -right-20 -top-20 h-96 w-96 rounded-full bg-brand/15 blur-3xl" />
          <div className="relative">
            <SectionHead kicker="Application mobile" title="MyMusic IA, bientôt sur ton téléphone."
                         text="La même expérience de création, pensée pour Android et iOS. Ton compte, tes crédits et ta bibliothèque te suivent partout." />
            <div className="mx-auto grid max-w-2xl grid-cols-2 items-start gap-4 sm:gap-8">
              <Shot src={APP_SCREENS.androidHome} alt="MyMusic IA sur Android — écran d'accueil" testId="mobile-shot-home" />
              <Shot src={APP_SCREENS.androidCreate} alt="MyMusic IA sur Android — création musicale" className="mt-10 sm:mt-16" testId="mobile-shot-create" />
            </div>
            <div className="mx-auto mt-12 flex max-w-xl flex-col gap-3 sm:flex-row sm:justify-center">
              <StoreBadge app={MOBILE_APPS.android} label="Google Play" testId="store-badge-android" />
              <StoreBadge app={MOBILE_APPS.ios} label="l'App Store" testId="store-badge-ios" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const PERKS = ['10 crédits inclus', '1 génération musicale = 1 crédit', 'Possibilité de participer aux challenges musicaux', 'Accès complet aux fonctionnalités de MyMusic IA'];

export function Pricing() {
  return (
    <section id="tarifs" className="scroll-mt-24 py-24" data-testid="landing-pricing">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <SectionHead kicker="Tarifs" title="Des crédits, sans abonnement"
                     text="Avec MyMusic IA, aucun abonnement mensuel ! Recharge tes crédits uniquement lorsque tu souhaites créer de nouvelles musiques." />
        <div className="relative mx-auto max-w-md overflow-hidden rounded-[2rem] border border-brand/40 bg-gradient-to-b from-[#1d1608] to-surface-app p-8 shadow-[0_40px_90px_-30px_rgba(255,165,0,0.35)] sm:p-10" data-testid="pricing-card">
          <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-brand/20 blur-3xl" />
          <p className="relative flex items-center justify-center gap-2 font-mono text-xs uppercase tracking-widest text-brand"><Sparkles className="h-4 w-4" /> Pack 10 crédits</p>
          <p className="relative mt-4 text-center font-display text-5xl font-extrabold text-white">1 000 <span className="text-2xl text-white/60">FCFA</span></p>
          <ul className="relative mt-8 space-y-3">
            {PERKS.map((p) => (
              <li key={p} className="flex items-start gap-3 text-sm text-white/85">
                <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-brand/15"><Check className="h-3.5 w-3.5 text-brand" /></span>{p}
              </li>
            ))}
          </ul>
          <Link to="/credits" data-testid="pricing-cta"
                className="relative mt-9 flex w-full items-center justify-center rounded-full bg-brand py-4 font-semibold text-black btn-glow transition-transform hover:scale-[1.02]">
            Obtenir mes crédits
          </Link>
        </div>
      </div>
    </section>
  );
}

const FAQ = [
  ['Comment créer une musique ?', "Connecte-toi, décris ton idée (histoire, émotion, occasion), choisis un style puis lance la création. Ta chanson apparaît dans ta bibliothèque dès qu'elle est prête."],
  ['Quels styles musicaux sont disponibles ?', "13 styles : Gospel, Musique urbaine, Musique personnalisée urbaine, Love Music personnalisée, Spot d'entreprise, Meilleurs vœux personnalisés, Rap Ivoire, Zouk, Zouglou, RnB, Coupé-Décalé, Naija Music et Drill Music."],
  ['Comment fonctionnent les crédits ?', "Chaque génération utilise 1 crédit ; en cas d'échec technique, le crédit est restitué. Un crédit d'essai est offert à l'inscription. Sur le site Web, le pack de 10 crédits coûte 1 000 FCFA."],
  ['Mes créations sont-elles publiques ?', 'Non. Tes musiques restent privées dans ta bibliothèque. Elles ne deviennent publiques que si tu choisis de les publier dans un challenge.'],
  ['Comment créer un compte ?', "Inscris-toi avec ton adresse e-mail et un mot de passe, ou en un clic avec ton compte Google."],
  ['Puis-je supprimer mes musiques ou mon compte ?', 'Oui. Tu peux supprimer chaque musique depuis ta bibliothèque, ou demander la suppression de ton compte (délai de récupération de 30 jours).'],
];

export function Faq() {
  return (
    <section id="faq" className="scroll-mt-24 py-24" data-testid="landing-faq">
      <div className="mx-auto max-w-3xl px-5 sm:px-8">
        <SectionHead kicker="FAQ" title="Questions fréquentes" text={`Une autre question ? Écris-nous à ${SUPPORT_EMAIL}.`} />
        <Accordion type="single" collapsible className="w-full">
          {FAQ.map(([q, a], i) => (
            <AccordionItem key={q} value={`q${i}`} className="border-white/10">
              <AccordionTrigger className="text-left font-display text-base text-white hover:text-brand hover:no-underline" data-testid={`faq-q-${i}`}>{q}</AccordionTrigger>
              <AccordionContent className="text-sm leading-relaxed text-white/65">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

const FOOT = [['/privacy-policy', 'Confidentialité'], ['/terms', 'CGU'], ['/delete-account', 'Suppression de compte'], ['/login', 'Connexion'], ['/register', 'Inscription']];

export function LandingFooter() {
  return (
    <footer className="border-t border-white/5 pb-10 pt-14" data-testid="landing-footer">
      <div className="mx-auto flex max-w-6xl flex-col items-center gap-10 px-5 text-center sm:px-8 md:flex-row md:items-start md:justify-between md:text-left">
        <div className="flex flex-col items-center md:items-start">
          <Logo variant="full" className="h-12" />
          <p className="mt-3 text-sm text-white/50">Le studio de ton imagination</p>
        </div>
        <nav className="flex flex-col items-center gap-2.5 text-sm text-white/60 md:items-start" aria-label="Liens légaux et compte">
          {FOOT.map(([to, l]) => <Link key={to} to={to} className="hover:text-brand" data-testid={`footer-link-${to.slice(1)}`}>{l}</Link>)}
        </nav>
      </div>
      <p className="mx-auto mt-12 max-w-6xl px-5 text-center text-xs text-white/30 sm:px-8" data-testid="footer-copyright">© {new Date().getFullYear()} MyMusic IA — ZOROBABEL. Tous droits réservés.</p>
    </footer>
  );
}
